import {
  annotationToolApiV1,
  getAnnotationToolBasePath,
  type ListResponse,
  type ObjectResponse,
} from '@verantos-dev/core-annotation-tool';

import { ACCURACY_JOBS_TAG } from '../constants';
import type { TriggerAccuracyRequest } from '../types/request';
import type {
  AccuracyBatchOption,
  AccuracyBranchesResponse,
  AccuracyJobSummaryRow,
  AccuracyModelOption,
  AccuracyPromptBody,
  AccuracyPromptOption,
  AccuracyRunHistoryRow,
  TriggerAccuracyJobResult,
} from '../types/response';

interface CancelAccuracyJobResult {
  accuracyJobId: string;
  status: string;
}

const basePath = () => `/internal-message-gateway${getAnnotationToolBasePath()}`;

// This slice must never invalidate ADMIN_PROJECTS_TAG (see AdminProjectsApiSlice's
// ADMIN_PROJECTS_TAG) — that tag is shared by six endpoints and, combined with the
// Projects grid's `loadOnFetch`, would blank the whole table on every accuracy call.
export const accuracyApiSlice = annotationToolApiV1
  .enhanceEndpoints({
    addTagTypes: [ACCURACY_JOBS_TAG],
  })
  .injectEndpoints({
    overrideExisting: true,
    endpoints: (builder) => ({
      getBatchesForAccuracy: builder.query<
        ListResponse<AccuracyBatchOption>,
        { projectId: string }
      >({
        query: ({ projectId }) => `${basePath()}/admin-projects/${projectId}/batches`,
      }),

      getAccuracyModels: builder.query<ListResponse<AccuracyModelOption>, void>({
        query: () => `${basePath()}/accuracy/config/models`,
      }),

      // TODO: cleanup with cp_pick_ai_prompt_branch ff
      // Branches of the verantos-dev/ai-prompts repo for the branch picker;
      // fetched only when cp_pick_ai_prompt_branch is on.
      getAccuracyBranches: builder.query<ObjectResponse<AccuracyBranchesResponse>, void>({
        query: () => `${basePath()}/accuracy/config/branches`,
      }),

      // TODO: cleanup with cp_trigger_accuracy_from_ui ff
      // Prompts are the directories under the project's org IDN in the
      // verantos-dev/ai-prompts repo, fetched realtime from the given branch
      // (default `main`). `branch` is only sent when the branch picker flag
      // is on so the flag-off request stays byte-identical.
      getAccuracyPrompts: builder.query<
        ListResponse<AccuracyPromptOption>,
        { projectId: string; branch?: string }
      >({
        query: ({ projectId, branch }) =>
          `${basePath()}/accuracy/config/prompts?projectId=${encodeURIComponent(projectId)}` +
          (branch ? `&branch=${encodeURIComponent(branch)}` : ''),
      }),

      // TODO: cleanup with cp_trigger_accuracy_from_ui ff
      // `promptPath` is the canonical repo path under the IDN (may be nested,
      // e.g. `stroke/experimental`) — encode per-segment so slashes survive.
      getAccuracyPromptBody: builder.query<
        ObjectResponse<AccuracyPromptBody>,
        { projectId: string; promptPath: string; branch?: string }
      >({
        query: ({ projectId, promptPath, branch }) =>
          `${basePath()}/accuracy/config/prompts/${promptPath
            .split('/')
            .map(encodeURIComponent)
            .join('/')}?projectId=${encodeURIComponent(projectId)}` +
          (branch ? `&branch=${encodeURIComponent(branch)}` : ''),
      }),

      triggerAccuracy: builder.mutation<
        ObjectResponse<TriggerAccuracyJobResult>,
        TriggerAccuracyRequest
      >({
        query: (body) => ({
          url: `${basePath()}/accuracy/trigger`,
          method: 'POST',
          body,
        }),
        invalidatesTags: [ACCURACY_JOBS_TAG],
      }),

      // TODO: cleanup with cp_trigger_accuracy_from_ui ff
      // Returns the latest job per project for the caller's tenant. Subscribed with
      // polling by useAccuracyJobColumn (screen-level, one subscription for the whole
      // grid) and fetched on demand (lazily) by useViewAccuracyDashboard to resolve a
      // project's optimizationProjectId.
      getLatestAccuracyJobs: builder.query<ListResponse<AccuracyJobSummaryRow>, void>({
        query: () => `${basePath()}/accuracy/jobs/latest`,
        providesTags: [ACCURACY_JOBS_TAG],
      }),

      // TODO: cleanup with cp_trigger_accuracy_from_ui ff
      // Full run history for one project, newest first. Fetched on demand by the
      // Accuracy runs modal; not polled.
      getAccuracyJobsByProject: builder.query<ListResponse<AccuracyRunHistoryRow>, string>({
        query: (projectId) => `${basePath()}/accuracy/jobs/${projectId}`,
        providesTags: [ACCURACY_JOBS_TAG],
      }),

      // TODO: cleanup with cp_trigger_accuracy_from_ui ff
      // Cancels a queued or in-progress run. The backend returns 409 when the run
      // is no longer cancellable; callers surface that to the user.
      cancelAccuracyJob: builder.mutation<ObjectResponse<CancelAccuracyJobResult>, string>({
        query: (accuracyJobId) => ({
          url: `${basePath()}/accuracy/jobs/${accuracyJobId}/cancel`,
          method: 'POST',
        }),
        invalidatesTags: [ACCURACY_JOBS_TAG],
      }),
    }),
  });

export const {
  useGetBatchesForAccuracyQuery,
  useGetAccuracyModelsQuery,
  useGetAccuracyBranchesQuery,
  useGetAccuracyPromptsQuery,
  useGetAccuracyPromptBodyQuery,
  useTriggerAccuracyMutation,
  useGetLatestAccuracyJobsQuery,
  useLazyGetLatestAccuracyJobsQuery,
  useGetAccuracyJobsByProjectQuery,
  useCancelAccuracyJobMutation,
} = accuracyApiSlice;
